import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingFeedbackHelmet from 'common/helmets/LandingFeedbackHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import FeaturesChildPagePagination from 'common/www/FeaturesChildPagePagination';
import Point4_1x from 'img/landing/feature-highlight-1-1x.webp';
import Point4_2x from 'img/landing/feature-highlight-1-2x.webp';
import Point1 from 'img/landing/feature-request-tracking-1.webp';
import Point2 from 'img/landing/feature-request-tracking-2.webp';
import Point3 from 'img/landing/feature-request-tracking-3.webp';
import 'css/components/www/_LandingFeaturesChildPage.scss';

class LandingFeedbackPage extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Feedback Child Page');
  }

  render() {
    return (
      <div className="landingFeedbackPage landingFeaturesChildPage">
        <LandingFeedbackHelmet />
        <div className="fullWidthContainer">
          <div className="header">
            <div className="text">
              <h1>Collect customer feedback and discover valuable product&nbsp;insights</h1>
              <p className="subheading">
                Understand what your customers want so you can build the features that matter most
                to&nbsp;them.
              </p>
              <Link className="LandingGlobal__cta" to="/register">
                Get Started
              </Link>
            </div>
          </div>
        </div>
        <main className="mainContentContainer">
          <h2>Canny is your team's home for collecting product&nbsp;feedback</h2>
          <div className="point">
            <LazyLoadedImage
              loading="eager"
              src={Point1}
              alt="Feedback collected and organized in a feature voting board"
            />
            <div className="text">
              <h2 className="title">
                Organize customer feedback with a product feature voting&nbsp;board
              </h2>
              <p>
                Collect feedback publicly or privately in a dedicated feedback board. Over time, the
                most popular ideas will bubble up to the&nbsp;top.
              </p>
              <ul>
                <li>Automatically group similar&nbsp;requests</li>
                <li>Pin important updates at the top of any post</li>
                <li>Reach out to everyone with the same request & enable&nbsp;discussion</li>
                <li>Close the product feedback loop with automated&nbsp;updates</li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point3}
              alt="Existing customer profiles including feedback they submitted"
            />
            <div className="text">
              <h2 className="title">Use existing user&nbsp;accounts</h2>
              <p>
                Your customers don't need to sign up for Canny. Connect their existing user accounts
                to know who is giving you&nbsp;feedback.
              </p>
              <ul>
                <li>
                  Install <a href="https://developers.canny.io/install">Canny Identify</a>—it's
                  easy!
                </li>
                <li>See all votes from a specific user on their&nbsp;profile</li>
                <li>
                  Include additional information like monthly spend, company type, or your own
                  custom&nbsp;fields
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point2}
              alt="Add feedback on behalf of your customers via different sources"
            />
            <div className="text">
              <h2 className="title">Collect feedback from other&nbsp;sources</h2>
              <p>
                You can collect feedback manually or by using one of{' '}
                <Link to="/integrations">our&nbsp;integrations</Link>. Every customer-facing team
                member should capture the feedback they hear in other&nbsp;channels.
              </p>
              <ul>
                <li>
                  Break down silos between teams—Canny is a great middle layer between
                  customer-facing and product&nbsp;teams
                </li>
                <li>Create a single process for collecting feedback across the whole&nbsp;team</li>
                <li>
                  Centralize feedback from user interviews, customer service, sales calls, social
                  media, review sites, contact forms, online surveys, and anywhere else you
                  get&nbsp;feedback
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point4_1x}
              srcSet={`${Point4_1x} 1x, ${Point4_2x} 2x`}
              alt="Instantly reply to several customers at once to show you're listening"
            />
            <div className="text">
              <h2 className="title">Improve customer satisfaction through&nbsp;listening</h2>
              <p>
                Getting actionable customer feedback helps you address customer needs and show you
                care — that wins you loyal&nbsp;customers.
              </p>
              <ul>
                <li>
                  Encourage customers to leave feedback on your board, vote for existing ideas, and
                  discuss with other users
                </li>
                <li>
                  Let your team discuss ideas with customers, understand their pain points, and keep
                  them updated
                </li>
                <li>
                  Create a public roadmap so customers can see when their features requests will be
                  worked&nbsp;on
                </li>
              </ul>
            </div>
          </div>
          <FeaturesChildPagePagination currentPage="feedback" />
          <LandingFooterCTA style="solid" expanded={true} />
          <FAQs
            faqs={[
              {
                question: 'How much does it cost?',
                answer:
                  "<p>We have plans to fit most companies' needs. You can get started with our Free plan which has everything you need to get started with feedback management. Learn more on <a href='https://canny.io/pricing' target='_blank' rel='noopener'>our pricing page</a>.</p>",
                html: (
                  <p>
                    We have plans to fit most companies' needs. You can get started with our Free
                    plan which has everything you need to get started with feedback management.
                    Learn more on{' '}
                    <a href="https://canny.io/pricing" target="_blank" rel="noopener">
                      our pricing page
                    </a>
                    .
                  </p>
                ),
              },
              {
                question: 'How can Canny help me collect feedback?',
                answer:
                  '<p>Canny is built to manage user feedback. You can easily set up a feedback board and start inviting your customers to leave their feedback via your feedback form. Plus, with multiple integrations to customer support and CRM tools, you can collect feedback wherever you interact with customers. Set your product team and customer support up for success.</p>',
                html: (
                  <p>
                    Canny is built to manage user feedback. You can easily set up a feedback board
                    and start inviting your customers to leave their feedback via your feedback
                    form. Plus, with multiple integrations to customer support and CRM tools, you
                    can collect feedback wherever you interact with customers. Set your product team
                    and customer support up for success.
                  </p>
                ),
              },
              {
                question: 'Can Canny help me uncover customer insights?',
                answer:
                  '<div><p>Absolutely! Canny has tools to help you group related posts together, so you get a true sense of how in demand a feature is. You can also build customer segments based on custom fields that you specify. That helps you understand what features are important to which customer segments. That can help you prioritize which features to focus on.</p><p>For example, you might choose to focus on building features for your unhappy customer segment to help customer retention. Or, you might build features that the potential customer segment has asked your sales team for.</p><p> Understanding which customers are leaving feedback helps make your feedback actionable.</p></div>',
                html: (
                  <>
                    <p>
                      Absolutely! Canny has tools to help you group related posts together, so you
                      get a true sense of how in demand a feature is. You can also build customer
                      segments based on custom fields that you specify. That helps you understand
                      what features are important to which customer segments. That can help you
                      prioritize which features to focus on.
                    </p>
                    <p>
                      For example, you might choose to focus on building features for your unhappy
                      customers to help customer retention. Or, you might build features that the
                      potential customer segment has asked your sales team for.{' '}
                    </p>
                    <p>
                      {' '}
                      Understanding which customers are leaving feedback helps make your feedback
                      actionable. Listening to your users throughout the customer journey also
                      improves retention.
                    </p>
                  </>
                ),
              },
              {
                question:
                  'How can collecting feedback through Canny help me improve customer satisfaction?',
                answer:
                  "<p>Customer satisfaction increases when your users feel heard. Give them a way to submit their feedback, and they'll feel that you care about their needs. If you follow through on their ideas, they'll become loyal and happy customers. Feedback collection is the first step to boosting your customer retention and decreasing customer churn.</p>",
                html: (
                  <p>
                    Customer satisfaction increases when your users feel heard. Give them a way to
                    submit their feedback, and they'll feel that you care about their needs. If you
                    follow through on their ideas, they'll become loyal and happy customers.
                    Feedback collection is the first step to boosting your customer retention and
                    decreasing customer churn.
                  </p>
                ),
              },
            ]}
          />
        </main>
      </div>
    );
  }
}

export default withContexts({ trackEvent: TrackEventContext })(LandingFeedbackPage);
